import type {
  AssistantMessageMeta,
  ReasoningRow,
  ThinkingState,
} from './types';

/**
 * Append a row to thinking state, draining any buffered reasoning onto it.
 * Implements the "reasoning attached to its next action" pattern — the text
 * the model emits right before a tool call is the reasoning for that
 * tool call.
 */
export function appendRow(
  prev: AssistantMessageMeta,
  buildRow: (pendingReasoning: string | undefined) => ReasoningRow,
  _stepLabel: string,
  init?: { title?: string }
): AssistantMessageMeta {
  const buffered = prev.thinking?.pendingReasoning ?? '';
  const pending = buffered.trim() ? buffered : undefined;
  const row = buildRow(pending);
  const thinking: ThinkingState = prev.thinking
    ? {
        ...prev.thinking,
        rows: [...prev.thinking.rows, row],
        pendingReasoning: '',
      }
    : {
        title: init?.title ?? 'Reasoning',
        rows: [row],
        pendingReasoning: '',
        isComplete: false,
      };
  return { ...prev, thinking };
}

/**
 * Append a step to the thinking state of a specific sub-ask, identified by
 * subAskId. When the sub-ask has not yet been announced via
 * `data-sub-ask-start`, a new ThinkingState is created on the fly so events
 * that arrive out of order are not dropped. The step text becomes a
 * `reasoning` row (V1's flat-`steps` array is gone — rows are canonical).
 */
export function appendStepToSubAsk(
  prev: AssistantMessageMeta,
  subAskId: string,
  stepLabel: string,
  init?: {
    title?: string;
    label?: string;
    groupKey?: string;
    groupLabel?: string;
  }
): AssistantMessageMeta {
  const list = prev.subAskThinkings;
  const idx = list.findIndex((t) => t.subAskId === subAskId);
  const newRow: ReasoningRow = {
    kind: 'reasoning',
    stepIndex: 0,
    text: stepLabel,
    groupKey: init?.groupKey,
    groupLabel: init?.groupLabel,
  };
  if (idx === -1) {
    const created: ThinkingState = {
      title: init?.title ?? init?.label ?? 'Reasoning',
      label: init?.label,
      rows: [newRow],
      pendingReasoning: '',
      isComplete: false,
      subAskId,
    };
    return { ...prev, subAskThinkings: [...list, created] };
  }
  const existing = list[idx];
  if (!existing) return prev;
  const updated: ThinkingState = {
    ...existing,
    rows: [...existing.rows, { ...newRow, stepIndex: existing.rows.length }],
  };
  const next = list.slice();
  next[idx] = updated;
  return { ...prev, subAskThinkings: next };
}
